/**
 * `useWidgetSocket` — the widget's realtime binding over the shared reconnecting
 * `WsClient` (ARCHITECTURE §6, §7).
 *
 * Connects to `/ws/widget`, authenticated by the opaque session token (NOT a
 * principal JWT — ARCHITECTURE §4). Subscribes to the typed domain events
 * relevant to a single visitor session (`message.created`, `session.closed`,
 * `typing`) and pushes them into `visitorStore`.
 *
 * The transport concerns — exponential backoff + jitter, heartbeat,
 * resume-since-lastEventId, offline outbound queue — all live in the shared
 * `WsClient`; this hook only wires URL + token + event handlers.
 */

import { useEffect, useState } from 'react';
import { WsClient, type WsConnectionState } from '@flozic/shared/ws';
import type { Message } from '@flozic/shared/types';
import { visitorStore } from '../session/visitorStore';
import { widgetEnv } from '../config/env';
import { playChime } from '../sound';
import { startCobrowse, stopCobrowse } from '../cobrowse/cobrowse';

/** WebSocket origin (dev-default ws://localhost:8000; see `config/env.ts`). */
const WS_BASE_URL: string = widgetEnv.wsBaseUrl;

/** The live socket, exposed so the composer can send typing frames (null while closed). */
let currentClient: WsClient | null = null;

export function getWidgetWs(): WsClient | null {
  return currentClient;
}

/** Build a locally-inserted system notice (agent connected, etc.). */
function systemMessage(sessionId: string, body: string): Message {
  return {
    id: `sys_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    sessionId,
    channel: 'inbuilt',
    senderType: 'system',
    direction: 'outbound',
    senderId: null,
    body,
    media: [],
    deliveryStatus: 'delivered',
    providerMessageId: null,
    clientMsgId: null,
    createdAt: new Date().toISOString(),
  };
}

/**
 * Open (and own) the widget WebSocket while a session token is present.
 *
 * @param sessionToken the opaque widget session token, or null when there is no
 *   live session yet (the socket stays closed until one exists).
 * @param cid the embed id (`ORG-WIDGET`) — needed by the co-browse controller to
 *   ship frames back over the public REST surface.
 * @returns the current connection state, for UI (e.g. a "reconnecting…" hint).
 */
export function useWidgetSocket(sessionToken: string | null, cid?: string): WsConnectionState {
  const [connectionState, setConnectionState] = useState<WsConnectionState>('idle');

  useEffect(() => {
    if (!sessionToken) {
      setConnectionState('idle');
      return;
    }

    const client = new WsClient({
      // The widget WS is authenticated by the session token as a query param,
      // (re)read on each (re)connect attempt. The backend mounts the WS router
      // under `/api/v1` (api/router.py), so the widget socket lives at
      // `/api/v1/ws/widget`; `WS_BASE_URL` is the origin only (see .env.example).
      url: () => `${WS_BASE_URL}/api/v1/ws/widget?token=${encodeURIComponent(sessionToken)}`,
      onError: (err) => {
        // eslint-disable-next-line no-console
        console.warn('[lc-widget] ws error', err);
      },
    });

    const unsubState = client.onStateChange(setConnectionState);

    // Domain events → store. Same event contract as backend `realtime/events.py`.
    const unsubMessage = client.on('message.created', (payload) => {
      // Only messages for our session (the server should already scope this).
      visitorStore.upsertMessage(payload.message);
      // Chime on an agent/bot reply (not the visitor's own echo or system notices), honoring the
      // header Sound toggle.
      if (payload.message.senderType === 'agent' || payload.message.senderType === 'bot') {
        playChime();
      }
      if (payload.session) {
        const current = visitorStore.getSnapshot().session;
        if (current) visitorStore.setSession({ ...current, ...payload.session });
      }
    });

    // An agent claimed the broadcast → tell the visitor they're connected and
    // reflect the new status/owner on the session summary.
    const unsubClaimed = client.on('session.claimed', (payload) => {
      visitorStore.upsertMessage(
        systemMessage(payload.sessionId, "You're now connected to an agent."),
      );
      const current = visitorStore.getSnapshot().session;
      if (current) {
        visitorStore.setSession({
          ...current,
          status: payload.status,
          claimedByAgentId: payload.claimedByAgentId,
          agentCallable: payload.agentCallable ?? false,
        });
      }
    });

    // Reassignment (transfer) → keep the session summary status in sync.
    const unsubAssigned = client.on('session.assigned', (payload) => {
      const current = visitorStore.getSnapshot().session;
      if (current) {
        visitorStore.setSession({
          ...current,
          status: payload.status,
          claimedByAgentId: payload.assignedToAgentId,
          agentCallable: payload.agentCallable ?? false,
        });
      }
    });

    const unsubClosed = client.on('session.closed', () => {
      stopCobrowse();
      visitorStore.setPhase('ended');
    });

    // Agent started/stopped co-browse from the dashboard ⋮ menu.
    const unsubCobrowse = client.on('cobrowse.control', (payload) => {
      const current = visitorStore.getSnapshot().session;
      if (!current || payload.sessionId !== current.id) return;
      if (payload.action === 'start' && cid) startCobrowse(cid, current.id);
      else if (payload.action === 'stop') stopCobrowse();
    });

    // Agent typing → the three-dot bubble. The visitor's OWN typing echoes back on the
    // session bus too (it mirrors for co-browse tabs), so filter to actor=agent.
    const unsubTyping = client.on('typing', (payload) => {
      if (payload.actor === 'agent') visitorStore.setAgentTyping(payload.isTyping);
    });

    client.connect();
    currentClient = client;

    return () => {
      unsubMessage();
      unsubClaimed();
      unsubAssigned();
      unsubClosed();
      unsubTyping();
      unsubCobrowse();
      unsubState();
      stopCobrowse();
      if (currentClient === client) currentClient = null;
      client.close();
    };
  }, [sessionToken, cid]);

  return connectionState;
}
